class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Dispara evento custom indicando que as sections foram substituídas (para rebind de cupons / atualizar totais)
    try {
      document.dispatchEvent(
        new CustomEvent('aomai:sections-replaced', {
          detail: { sections: Object.keys(parsedState.sections || {}), parsedState }
        })
      );
    } catch (e) {
      console.warn('[AOMAI] Falha ao disparar aomai:sections-replaced', e);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Funcionalidade de drag scroll para produtos recomendados
class DragScrollHandler {
  constructor() {
    this.init();
  }

  init() {
    // Aguarda o DOM estar pronto e observa mudanças no cart drawer
    document.addEventListener('DOMContentLoaded', () => this.setupDragScroll());
    
    // Observer para detectar quando novos elementos são adicionados
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          this.setupDragScroll();
        }
      });
    });

    // Observa mudanças no body para capturar quando o cart drawer é renderizado
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  setupDragScroll() {
    const containers = document.querySelectorAll('.cart-drawer__recommendations--with-items .cart-drawer__mini-products');
    
    containers.forEach(container => {
      // Remove listeners anteriores se existirem
      if (container.dragScrollSetup) return;
      
      let isDown = false;
      let startX;
      let scrollLeft;

      const handleMouseDown = (e) => {
        isDown = true;
        container.style.cursor = 'grabbing';
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        e.preventDefault();
      };

      const handleMouseLeave = () => {
        isDown = false;
        container.style.cursor = 'grab';
      };

      const handleMouseUp = () => {
        isDown = false;
        container.style.cursor = 'grab';
      };

      const handleMouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 2; // Velocidade do scroll
        container.scrollLeft = scrollLeft - walk;
      };

      // Touch events para dispositivos móveis
      const handleTouchStart = (e) => {
        isDown = true;
        startX = e.touches[0].pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
      };

      const handleTouchEnd = () => {
        isDown = false;
      };

      const handleTouchMove = (e) => {
        if (!isDown) return;
        const x = e.touches[0].pageX - container.offsetLeft;
        const walk = (x - startX) * 2;
        container.scrollLeft = scrollLeft - walk;
      };

      // Adiciona event listeners
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('mouseup', handleMouseUp);
      container.addEventListener('mousemove', handleMouseMove);
      
      // Touch events
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });

      // Previne seleção de texto durante o drag
      container.addEventListener('selectstart', (e) => e.preventDefault());

      // Marca como configurado
      container.dragScrollSetup = true;
    });
  }
}

// Inicializa o handler de drag scroll
new DragScrollHandler();

// ================= AOMAI: Módulo persistente de totais e descontos =================
(() => {
  const FLAG = '__aomaiDiscountTotalsModule';
  if (window[FLAG]) return; // singleton
  window[FLAG] = true;

  const currency =
    document.documentElement.getAttribute('data-shop-currency') ||
    (window.Shopify && (Shopify.currency && (Shopify.currency.active || Shopify.currency.current))) ||
    'BRL';
  const ROOT = () => (window.Shopify?.routes?.root || '/');
  const DISCOUNT_SVG = '<svg class="icon icon-discount" viewBox="0 0 12 12"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"/></svg>';

  const formatMoney = (c) => {
    try {
      return (c / 100).toLocaleString('pt-BR', { style: 'currency', currency });
    } catch (e) {
      return 'R$ ' + (c / 100).toFixed(2).replace('.', ',');
    }
  };
  const escapeHtml = (s = '') =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const fetchCart = async () => {
    const r = await fetch(ROOT() + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('Falha cart.js');
    return r.json();
  };

  const getEls = () => {
    const discountsEl = document.getElementById('aomaiCartDiscounts');
    const totalEl = document.getElementById('aomaiCartTotal');
    if (discountsEl && totalEl) return { discountsEl, totalEl };
    return null;
  };

  const renderDiscounts = (cart, els) => {
    const { discountsEl } = els;
    const apps = cart.cart_level_discount_applications || [];
    if (!apps.length) {
      discountsEl.innerHTML = '';
      return;
    }
    const ul = document.createElement('ul');
    ul.className = 'discounts list-unstyled';
    ul.setAttribute('role', 'list');
    ul.setAttribute('aria-label', 'Descontos');
    apps.forEach((app) => {
      const li = document.createElement('li');
      li.className = 'discounts__discount discounts__discount--end';
      li.innerHTML = `${DISCOUNT_SVG} ${escapeHtml((app.title || '').toUpperCase())} (-${formatMoney(
        app.total_allocated_amount
      )})`;
      ul.appendChild(li);
    });
    discountsEl.innerHTML = '';
    discountsEl.appendChild(ul);
  };

  const setPending = (code, els) => {
    const { discountsEl } = els;
    if (!code || discountsEl.children.length) return;
    discountsEl.innerHTML = `<ul class="discounts list-unstyled" role="list" aria-label="Descontos (pendente)"><li class="discounts__discount discounts__discount--end" data-pending="true">${DISCOUNT_SVG} ${escapeHtml(
      code.toUpperCase()
    )} (calculando…)</li></ul>`;
  };

  const updateUI = async (opts = {}) => {
    const els = getEls();
    if (!els) return; // aguarda próxima chamada
    if (opts.fakeCode) setPending(opts.fakeCode, els);
    try {
      const cart = await fetchCart();
      renderDiscounts(cart, els);
      els.totalEl.textContent = formatMoney(cart.total_price);
      document.dispatchEvent(
        new CustomEvent('aomai:cart:totals-updated', { detail: { cart } })
      );
    } catch (e) {
      console.warn('[AOMAI] Falha atualizar totais', e);
    }
  };

  // Eventos que disparam atualização
  ['aomai:discount-applied', 'aomai:discount-cleared', 'aomai:discount:cleared', 'cartRequestEvent'].forEach((ev) =>
    document.addEventListener(ev, () => updateUI())
  );
  document.addEventListener('aomai:discount-applied-pending', (e) =>
    updateUI({ fakeCode: e.detail?.code })
  );
  document.addEventListener('aomai:sections-replaced', () => updateUI());

  // Tentativa inicial (poll curto + observer leve)
  const tryInit = () => {
    if (getEls()) {
      updateUI();
      return true;
    }
    return false;
  };
  if (!tryInit()) {
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (tryInit() || tries > 40) clearInterval(interval);
    }, 300);
    const mo = new MutationObserver(() => {
      if (tryInit()) mo.disconnect();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Expor função manual
  window.__aomaiUpdateCartTotals = updateUI;
})();

// ================= AOMAI: Binder global de cupom persistente =================
(() => {
  const FLAG = '__aomaiCouponBinder';
  if (window[FLAG]) return; // singleton
  window[FLAG] = true;

  const ROOT = () => (window.Shopify?.routes?.root || '/');
  const gState = { busy: false };

  const discountApplied = (cart, code) => {
    const low = (code || '').toLowerCase();
    if (
      cart.cart_level_discount_applications?.some(
        (a) => (a.title || '').toLowerCase() === low
      )
    )
      return true;
    return cart.items?.some((i) =>
      i.discounts?.some((d) => (d.title || '').toLowerCase() === low)
    );
  };

  const applyDiscount = async (code) => {
    const r = await fetch(ROOT() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    });
    if (!r.ok) throw new Error('Falha ao aplicar: ' + r.status);
    return r.json().catch(() => ({}));
  };
  const fetchCart = async () => {
    const r = await fetch(ROOT() + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('Erro ao buscar carrinho');
    return r.json();
  };

  const bindInstance = (rootEl) => {
    if (rootEl.__couponBound) return; // evita duplicar
    const toggleBtn = rootEl.querySelector('#aomaiCupom');
    const wrapper = rootEl.querySelector('.aomai-discount-wrapper');
    const input = rootEl.querySelector('#aomai-discount-code');
    const applyBtn = rootEl.querySelector('#aomai-apply-discount');
    const messageEl = rootEl.querySelector('#aomai-discount-message');
    if (!(toggleBtn && wrapper && input && applyBtn && messageEl)) return; // incompleto
    rootEl.__couponBound = true;

    const showMsg = (t, type = 'info') => {
      messageEl.textContent = t;
      messageEl.style.display = 'block';
      messageEl.style.color =
        type === 'success' ? 'green' : type === 'error' ? 'red' : '#333';
    };
    const clearMsg = () => {
      messageEl.textContent = '';
      messageEl.style.display = 'none';
    };
    const open = () => {
      if (wrapper.dataset.expanded === 'true') return;
      wrapper.dataset.expanded = 'true';
      wrapper.style.height = wrapper.scrollHeight + 'px';
      setTimeout(() => {
        if (wrapper.dataset.expanded === 'true') wrapper.style.height = 'auto';
      }, 350);
    };
    const close = () => {
      if (wrapper.dataset.expanded !== 'true') return;
      if (wrapper.style.height === 'auto')
        wrapper.style.height = wrapper.scrollHeight + 'px';
      wrapper.offsetHeight; // force reflow
      wrapper.style.height = '0px';
      wrapper.dataset.expanded = 'false';
    };
    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (wrapper.dataset.expanded === 'true') close();
      else {
        open();
        setTimeout(() => input.focus(), 120);
      }
    });
    const setLoading = (b) => {
      applyBtn.disabled = b;
      applyBtn.style.opacity = b ? '.6' : '1';
      applyBtn.dataset.originalText = applyBtn.dataset.originalText || applyBtn.textContent;
      applyBtn.textContent = b ? 'Aplicando...' : applyBtn.dataset.originalText;
    };
    const submit = async () => {
      if (gState.busy) return;
      clearMsg();
      const code = (input.value || '').trim();
      if (!code) {
        showMsg('Digite um cupom', 'error');
        return;
      }
      try {
        gState.busy = true;
        setLoading(true);
        document.dispatchEvent(
          new CustomEvent('aomai:discount-applied-pending', { detail: { code } })
        );
        await applyDiscount(code);
        const cart = await fetchCart();
        if (discountApplied(cart, code)) {
          showMsg('Cupom aplicado com sucesso!', 'success');
          document.dispatchEvent(
            new CustomEvent('aomai:discount-applied', { detail: { code, cart } })
          );
        } else {
          showMsg('Cupom inválido ou expirado', 'error');
        }
      } catch (err) {
        showMsg(err.message || 'Erro ao aplicar cupom', 'error');
      } finally {
        gState.busy = false;
        setLoading(false);
      }
    };
    applyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      submit();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });
  };

  const scan = () => {
    document
      .querySelectorAll('[data-aomai-coupon-snippet]')
      .forEach((el) => bindInstance(el));
  };
  scan();

  // Rescan ao substituir sections
  document.addEventListener('aomai:sections-replaced', () => scan());

  // Fallback MutationObserver leve
  const mo = new MutationObserver(() => scan());
  mo.observe(document.documentElement, { childList: true, subtree: true });

  window.__aomaiCouponRebind = scan;
})();

// ================= AOMAI: Binder global de cálculo de frete =================
(() => {
  const FLAG = '__aomaiShippingBinder';
  if (window[FLAG]) return; // singleton
  window[FLAG] = true;

  const ROOT = () => (window.Shopify?.routes?.root || '/');
  // === Lógica original solicitada ===
  const getAddressByZip = async (zip) => {
    const res = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
    const data = await res.json();
    if (data.erro) throw new Error('CEP_INVALIDO');
    return { city: data.localidade, province: data.uf };
  };

  const getShippingRates = async (zip) => {
    const clean = (zip || '').replace(/\D/g, '');
    if (clean.length !== 8) return { success: false, error: 'ZIP_INVALID' };
    try {
      const address = await getAddressByZip(clean);
      const params = new URLSearchParams({
        'shipping_address[zip]': clean,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': address.province,
        'shipping_address[city]': address.city
      });
      const response = await fetch(`${ROOT()}cart/shipping_rates.json?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('FRETE_ERRO');
      const data = await response.json();
      return { success: true, rates: data.shipping_rates || [] };
    } catch (e) {
      return { success: false, error: e.message || 'FRETE_ERRO' };
    }
  };

  const MESSAGES = {
    CEP_INVALIDO: 'CEP inválido',
    ZIP_INVALID: 'Informe um CEP com 8 dígitos',
    FRETE_ERRO: 'Não foi possível calcular o frete',
    GENERIC: 'Erro ao calcular frete'
  };

  const formatMoneyFromDecimalString = (priceStr, currency = 'BRL') => {
    // priceStr vindo da API Shopify normalmente é string decimal (ex: "12.34")
    let num = 0;
    if (typeof priceStr === 'number') num = priceStr; else if (typeof priceStr === 'string') num = parseFloat(priceStr.replace(',', '.')) || 0;
    const cents = Math.round(num * 100);
    try { return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency }); } catch(e){ return 'R$ ' + (cents/100).toFixed(2).replace('.', ','); }
  };

  const bindInstance = (rootEl) => {
    if (rootEl.__shippingBound) return;
    const toggleBtn = rootEl.querySelector('#aomaiFrete');
    const wrapper = rootEl.querySelector('.aomai-shipping-wrapper');
    const zipInput = rootEl.querySelector('#aomai-shipping-zip');
    const calcBtn = rootEl.querySelector('#aomai-calc-shipping');
    const resultsEl = rootEl.querySelector('#aomai-shipping-results');
    if (!(toggleBtn && wrapper && zipInput && calcBtn && resultsEl)) return;
    rootEl.__shippingBound = true;

    const open = () => {
      if (wrapper.dataset.expanded === 'true') return;
      wrapper.dataset.expanded = 'true';
      wrapper.style.height = wrapper.scrollHeight + 'px';
      setTimeout(() => {
        if (wrapper.dataset.expanded === 'true') wrapper.style.height = 'auto';
      }, 350);
    };
    const close = () => {
      if (wrapper.dataset.expanded !== 'true') return;
      if (wrapper.style.height === 'auto') wrapper.style.height = wrapper.scrollHeight + 'px';
      wrapper.offsetHeight;
      wrapper.style.height = '0px';
      wrapper.dataset.expanded = 'false';
    };

    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (wrapper.dataset.expanded === 'true') close();
      else {
        open();
        setTimeout(() => zipInput.focus(), 120);
      }
    });

    const setLoading = (b) => {
      calcBtn.disabled = b;
      calcBtn.style.opacity = b ? '.6' : '1';
      calcBtn.dataset.originalText = calcBtn.dataset.originalText || calcBtn.textContent;
      calcBtn.textContent = b ? 'Calculando...' : calcBtn.dataset.originalText;
    };

    const showError = (code) => {
      const msg = MESSAGES[code] || MESSAGES.GENERIC;
      resultsEl.innerHTML = `<p style="color:red">${msg}</p>`;
    };
    const showRates = (rates) => {
      if (!rates.length) { resultsEl.innerHTML = '<p>Nenhuma opção de frete encontrada.</p>'; return; }
      const ul = document.createElement('ul'); ul.className = 'aomai-shipping-rates';
      rates.forEach(r => {
        const li = document.createElement('li');
        li.textContent = `${r.presentment_name || r.name} - ${formatMoneyFromDecimalString(r.price)}`;
        ul.appendChild(li);
      });
      resultsEl.innerHTML=''; resultsEl.appendChild(ul);
    };

    const cleanZip = (v) => v.replace(/\D/g, '').slice(0, 8);
    zipInput.addEventListener('input', () => {
      const digits = cleanZip(zipInput.value);
      // Formata como 00000-000 se tiver >5 dígitos
      zipInput.value = digits.length > 5 ? digits.slice(0, 5) + '-' + digits.slice(5) : digits;
    });

    const calc = async () => {
      resultsEl.innerHTML = '';
      const raw = zipInput.value;
      const digits = raw.replace(/\D/g, '');
      setLoading(true);
      document.dispatchEvent(new CustomEvent('aomai:shipping:pending', { detail: { zip: digits } }));
      const { success, rates, error } = await getShippingRates(digits);
      if (!success) {
        showError(error);
      } else {
        showRates(rates);
        document.dispatchEvent(new CustomEvent('aomai:shipping-calculated', { detail: { zip: digits, rates } }));
      }
      setLoading(false);
    };
    calcBtn.addEventListener('click', (e) => {
      e.preventDefault();
      calc();
    });
    zipInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        calc();
      }
    });
  };

  const scan = () => {
    document
      .querySelectorAll('[data-aomai-shipping-snippet]')
      .forEach((el) => bindInstance(el));
  };
  scan();

  document.addEventListener('aomai:sections-replaced', () => scan());
  const mo = new MutationObserver(() => scan());
  mo.observe(document.documentElement, { childList: true, subtree: true });

  window.__aomaiShippingRebind = scan;
})();
